// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_round_card/recap-season
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Round data stays local; only a DERIVED-SIGNALS
// object (scoring trend, fairway/GIR rates, courses played, bag evolution — see
// signals.rs) is sent for THIS recap call. Raw per-hole cards never leave the device.
//
// ONE billed call per action (CRUD narration, not an agentic loop): the recap is a
// single POST that narrates the on-device signals slice. There is no chat surface.
//
// THE FENCE (Round Card — CAN-only, observational). This MUST be real:
//   • HARD, PRE-LLM refusal: any coaching / advice ask — "what club should I hit",
//     "fix my slice", "how do I…", or a request for a tournament-eligibility / official
//     HANDICAP INDEX — is refused HERE, before any network call, deterministically.
//   • The recap NARRATES the computed structure only — it never coaches, never
//     suggests a club or a swing fix, and NEVER computes an official handicap index
//     (a regulated certification). The server prompt mirrors this; the client enforces
//     it pre-flight so a forbidden request never reaches (or is billed by) the brain.
// We do not rebuild the brain; we compute the signals, screen the request, call it,
// render the result.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { RoundSignals } from "./store";

const SERVER = "https://api.orioralabs.com";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

// Thrown by the PRE-LLM fence. Never reaches the network — the request is refused on-device.
export class FenceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FenceError";
  }
}

// ── THE HARD FENCE (pre-LLM, deterministic) ────────────────────────────────────
// Round Card is CAN-only / observational: it narrates the user's OWN logged rounds.
// It must NEVER coach (club selection, swing fixes, advice) and NEVER produce an
// official/tournament handicap index (a regulated cert). These patterns are refused
// BEFORE any model call. They screen any free text the app might ever carry into the
// recap (notes that read like a coaching question, or a future ask box). The recap
// itself sends only the derived-signals object, so this is defence-in-depth — but it
// is REAL: a matching request throws FenceError and no billed call is made.
const COACHING_PATTERNS: RegExp[] = [
  /\bwhat\s+club\b/i,
  /\bwhich\s+club\b/i,
  /\bclub\s+(should|do)\s+i\b/i,
  /\bshould\s+i\s+(hit|use|play|club|lay\s*up|go\s+for)\b/i,
  /\bfix\s+my\b/i,
  /\bfix\s+(my\s+)?(slice|hook|swing|putt|chip|grip|stance|shank|fade|draw)\b/i,
  /\bhow\s+(do|can|should)\s+i\b/i,
  /\bhow\s+to\s+(fix|improve|hit|stop|cure|swing|putt|chip|drive)\b/i,
  /\bcoach(ing)?\b/i,
  // "give me tips", "give me some/a few tips", "any pointers", "got any drills", "tips please"
  /\b(give|got|have|need|want)\b[\w\s]*?\b(advice|tips?|pointers?|drills?|lessons?)\b/i,
  /\bany\s+[\w\s]*?\b(advice|tips?|pointers?|drills?|lessons?)\b/i,
  /\b(advice|tips?|pointers?|drills?|lessons?)\s+(please|pls)\b/i,
  /\bwhat\s+should\s+i\s+(do|work\s+on|practice|change)\b/i,
  // swing/technique advice — "what's the right swing", "fix/improve my swing", "swing tip"
  /\b(right|correct|proper|good|best|ideal|fix|improve|change|work\s+on|help\s+with)\s+(my\s+)?swing\b/i,
  /\bswing\s+(tip|advice|fix|fault|path|plane|thought|change|help)\b/i,
];
const HANDICAP_PATTERNS: RegExp[] = [
  /\bhandicap\s+index\b/i,
  /\bofficial\s+handicap\b/i,
  /\bwhga\b/i,
  /\busga\s+handicap\b/i,
  /\bworld\s+handicap\b/i,
  /\bcourse\s+handicap\b/i,
  /\bhandicap\s+(cert|certificate|rating|calculation|number)\b/i,
  /\b(what|what's|whats|calculate|compute|my)\s+.*\bhandicap\b/i,
  /\btournament[-\s]?elig/i,
];

export type FenceVerdict =
  | { ok: true }
  | { ok: false; kind: "coaching" | "handicap"; message: string };

/**
 * Deterministic pre-LLM screen. Returns a refusal verdict for any coaching ask or
 * any request for an official handicap index. Used to gate the recap and to vet any
 * free text before it could ever reach the brain.
 */
export function fenceCheck(text: string): FenceVerdict {
  const t = (text ?? "").trim();
  if (!t) return { ok: true };
  for (const re of HANDICAP_PATTERNS) {
    if (re.test(t)) {
      return {
        ok: false,
        kind: "handicap",
        message:
          "Round Card narrates your logged rounds — it doesn't compute an official or tournament handicap index. That's a regulated certification you get from your golf federation or a handicap service.",
      };
    }
  }
  for (const re of COACHING_PATTERNS) {
    if (re.test(t)) {
      return {
        ok: false,
        kind: "coaching",
        message:
          "Round Card describes what you've logged — it doesn't give coaching, club picks, or swing fixes. For that, see a PGA professional.",
      };
    }
  }
  return { ok: true };
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/oriora_round_card/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface SeasonRecap {
  recap: string;
}

/**
 * Season recap. The ONE AI feature.
 *
 * We compute the non-obvious STRUCTURE on-device (scoring trend by month, recent form,
 * fairway/GIR rates + their direction, putting, courses played, conditions, partner
 * counts, and the reconstructed bag evolution — see store.ts / signals.rs) and send
 * ONLY those small aggregates. The brain then surfaces specific, named-number
 * observations across the multi-season archive that the golfer couldn't eyeball.
 * Raw per-hole cards never leave the device.
 *
 * The endpoint + Bearer JWT: POST /api/apps/oriora_round_card/recap-season. The server
 * prompt reads the `signals` object (operator task) and stays OBSERVATIONAL ONLY:
 * describe trends/rates/deltas/bag changes, never coach, never suggest a club/swing
 * fix, and never compute an official handicap index.
 *
 * The SYSTEM PROMPT the server must hold (mirrors the schema string in signals.rs):
 *   "You are the Round Card season recap. You are given a JSON object of DERIVED
 *    aggregates from one golfer's own logged rounds (scores normalized to 18 holes —
 *    LOWER is better — plus fairway/GIR rates, courses played, and bag add/retire
 *    events). Narrate the patterns you see in 2-4 sentences, naming specific figures
 *    (e.g. 'your scoring dropped ~4 strokes since spring', 'fairways-hit climbed from
 *    48% to 61%', 'you added a hybrid and retired the 3-iron'). You may ONLY describe
 *    this logged corpus. You MUST NOT: give coaching, tips, drills, or club/shot
 *    advice; diagnose a swing; or state/estimate/compute an official, course, or
 *    tournament handicap index (a regulated certification). If the data is thin, say
 *    so plainly. Output observation only."
 */
export async function recapSeason(signals: RoundSignals): Promise<SeasonRecap> {
  if (signals.overall.total_rounds === 0) {
    throw new BrainError("unavailable", "Log a few rounds first — there's nothing to recap yet.");
  }
  const r = await brainCall<Partial<SeasonRecap>>("recap-season", { signals });
  return { recap: String(r.recap ?? "").trim() };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_round_card/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/oriora_round_card/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/oriora_round_card/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile. Session returns via auth:deep-link (App.tsx + lib.rs). The web callback must
// allowlist `oriora-round-card` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");
